import { executeApiRequest } from "@/services/api/apiService";
import { useToastErrorHandler } from "@/composables/useToastErrorHandler";
import {
  ServiceRecordsApi,
  ServiceRecordsApiEndpoints,
  DocumentationApi,
} from "@/config/api/serviceRecords/serviceRecordsApi";
import type {
  ServiceRecord,
  DocumentationRecord,
  CreateServiceRecordDto,
  UpdateServiceRecordDto,
  CreateDocumentationDto,
} from "@/types/service-records";

const useServiceRecordsApiService = () => {
  const { withToastErrorHandling } = useToastErrorHandler();

  const fetchServiceRecords = async (params: {
    supplierId: number;
    offerId: number;
  }) => {
    return withToastErrorHandling(
      async () => {
        const result = await executeApiRequest<ServiceRecord[]>(
          {
            endpointKey: Symbol.for(
              ServiceRecordsApiEndpoints.GET_SUPPLIER_SERVICE_RECORDS,
            ),
            config: ServiceRecordsApi,
          },
          {
            pathParams: [
              "supplier",
              params.supplierId,
              "offer",
              params.offerId,
            ],
          },
        );
        return result;
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Servicerapportfeil",
          detail: "Kunne ikke hente servicerapporter",
        },
      },
    );
  };

  const createServiceRecord = async (
    supplierId: number,
    customerOrgNumber: string,
    serviceOfferId: number,
    data: CreateServiceRecordDto,
  ) => {
    return withToastErrorHandling(
      async () => {
        return executeApiRequest<ServiceRecord>(
          {
            endpointKey: Symbol.for(
              ServiceRecordsApiEndpoints.CREATE_SERVICE_RECORD,
            ),
            config: ServiceRecordsApi,
          },
          {
            pathParams: [
              "supplier",
              supplierId,
              "organization",
              customerOrgNumber,
              "offer",
              serviceOfferId,
            ],
            data: data,
          },
        );
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Serviceoppføringfeil",
          detail: "Kunne ikke opprette serviceoppføring",
        },
        successOptions: {
          severity: "success",
          summary: "Vellykket",
          detail: "Serviceoppføring opprettet",
        },
      },
    );
  };

  const updateServiceRecord = async (
    serviceRecordId: number,
    supplierId: number,
    customerOrgNumber: string,
    data: UpdateServiceRecordDto,
  ) => {
    return withToastErrorHandling(
      async () => {
        return executeApiRequest<ServiceRecord>(
          {
            endpointKey: Symbol.for(
              ServiceRecordsApiEndpoints.UPDATE_SERVICE_RECORD,
            ),
            config: ServiceRecordsApi,
          },
          {
            pathParams: [
              serviceRecordId,
              "supplier",
              supplierId,
              "organization",
              customerOrgNumber,
            ],
            data: data,
          },
        );
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Serviceoppføringfeil",
          detail: "Kunne ikke oppdatere serviceoppføring",
        },
        successOptions: {
          severity: "success",
          summary: "Vellykket",
          detail: "Serviceoppføring oppdatert",
        },
      },
    );
  };

  const markServiceRecordComplete = async (
    serviceRecordId: number,
    supplierId: number,
    customerOrgNumber: string,
  ) => {
    return withToastErrorHandling(
      async () => {
        try {
          return executeApiRequest<ServiceRecord>(
            {
              endpointKey: Symbol.for(
                ServiceRecordsApiEndpoints.MARK_SERVICE_RECORD_COMPLETE,
              ),
              config: ServiceRecordsApi,
            },
            {
              pathParams: [
                serviceRecordId,
                "supplier",
                supplierId,
                "organization",
                customerOrgNumber,
                "complete",
              ],
            },
          );
        } catch (error: unknown) {
          // DEBUGGING: Log the complete error structure
          // Log specific error properties we're checking
          const errorWithResponse = error as {
            response?: { data?: { message?: string } };
          };
          const errorWithMessage = error as { message?: string };

          // Check for specific error type and provide custom message
          const isServiceDateError =
            errorWithResponse?.response?.data?.message ===
              "ServiceDateNullOrInFutureError" ||
            errorWithMessage?.message?.includes(
              "ServiceDateNullOrInFutureError",
            );

          if (isServiceDateError) {
            throw new Error(
              "Servicedato må være satt og kan ikke være i fremtiden for å markere oppføringen som fullført",
            );
          }
          // Re-throw other errors to be handled by default error handling
          throw error;
        }
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Serviceoppføringfeil",
          detail: "Kunne ikke fullføre serviceoppføring",
        },
        successOptions: {
          severity: "success",
          summary: "Vellykket",
          detail: "Serviceoppføring markert som fullført",
        },
      },
    );
  };

  const deleteServiceRecord = async (
    serviceRecordId: number,
    supplierId: number,
    customerOrgNumber: string,
  ) => {
    return withToastErrorHandling(
      async () => {
        return executeApiRequest<void>(
          {
            endpointKey: Symbol.for(
              ServiceRecordsApiEndpoints.DELETE_SERVICE_RECORD,
            ),
            config: ServiceRecordsApi,
          },
          {
            pathParams: [
              serviceRecordId,
              "supplier",
              supplierId,
              "organization",
              customerOrgNumber,
            ],
          },
        );
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Serviceoppføringfeil",
          detail: "Kunne ikke slette serviceoppføring",
        },
        successOptions: {
          severity: "success",
          summary: "Vellykket",
          detail: "Serviceoppføring slettet",
        },
      },
    );
  };

  const createDocumentation = async (
    supplierId: number,
    supplierRequestId: number,
    serviceOfferId: number,
    data: CreateDocumentationDto[],
  ) => {
    return withToastErrorHandling(
      async () => {
        return executeApiRequest<DocumentationRecord>(
          {
            endpointKey: Symbol.for(
              ServiceRecordsApiEndpoints.CREATE_DOCUMENTATION,
            ),
            config: DocumentationApi,
          },
          {
            pathParams: [
              "supplier",
              supplierId,
              "supplierRequest",
              supplierRequestId,
              "offers",
              serviceOfferId,
              "AgreementAttachments",
            ],
            data: data,
          },
        );
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Dokumentasjonsfeil",
          detail: "Kunne ikke lagre dokumentasjon",
        },
        successOptions: {
          severity: "success",
          summary: "Vellykket",
          detail: "Dokumentasjon lagret",
        },
      },
    );
  };

  return {
    fetchServiceRecords,
    createServiceRecord,
    updateServiceRecord,
    markServiceRecordComplete,
    deleteServiceRecord,
    createDocumentation,
  };
};

export const useServiceRecordsApi = useServiceRecordsApiService;
